import { GraphQL17Alpha9Handler } from "@apollo/client/incremental";
import type { DocumentNode } from "graphql";

/**
 * A payload carrying the query's own data *and* deferred patches that already
 * resolved — what our server sends when a `@defer`red fragment finishes before
 * the initial payload is flushed.
 */
type CombinedChunk<TData> = GraphQL17Alpha9Handler.InitialResult<TData> &
  Required<
    Pick<GraphQL17Alpha9Handler.SubsequentResult<TData>, "incremental">
  > &
  Pick<GraphQL17Alpha9Handler.SubsequentResult<TData>, "completed">;

function isCombinedChunk<TData>(
  chunk: GraphQL17Alpha9Handler.Chunk<TData>
): chunk is CombinedChunk<TData> {
  return (
    "incremental" in chunk &&
    chunk.incremental !== undefined &&
    chunk.incremental.length > 0 &&
    "data" in chunk
  );
}

/**
 * `@defer` handler that tolerates two `GraphQL17Alpha9Handler` defects, both of
 * which surface as an empty table plus `AbortError: BodyStreamBuffer was
 * aborted` once a patch reaches the client in the initial payload.
 *
 * Apollo reads the query back from the cache before applying each chunk and
 * hands the diff to the handler as its merge target. That diff is `null`
 * whenever nothing readable is cached yet, and the handler merges into it
 * unguarded, so the query throws `TypeError: Cannot read properties of null`
 * and unsubscribing aborts the rest of the stream.
 *
 * The handler also treats `data` and `incremental` as mutually exclusive: given
 * both, it merges only the patches and silently drops the payload's own data.
 *
 * Both go away once Apollo null-checks the diff its `IncrementalRequest.handle`
 * signature already accepts and stops discarding data on combined payloads.
 */
class ResilientIncrementalHandler extends GraphQL17Alpha9Handler {
  startRequest<TData>(request: { query: DocumentNode }) {
    const incrementalRequest = super.startRequest<TData>(request);
    const handle = incrementalRequest.handle.bind(incrementalRequest);

    incrementalRequest.handle = (cacheData, chunk) => {
      // `undefined` means the same thing to the handler as an absent diff, but
      // falls back to the data it accumulated instead of merging into `null`.
      const mergeTarget = cacheData ?? undefined;

      if (!isCombinedChunk(chunk)) {
        return handle(mergeTarget, chunk);
      }

      // Feed it as the two payloads it expects: initial data first, patches on top.
      const { incremental, completed, ...initial } = chunk;
      handle(mergeTarget, initial);
      return handle(undefined, {
        hasNext: chunk.hasNext,
        incremental,
        completed,
      });
    };

    return incrementalRequest;
  }
}

export { ResilientIncrementalHandler };
